// Pure helpers of the Certificate card and the Vaults list warning. node --test src/lib/certificate.test.ts
import type { TlsInfo } from './api.ts';

export type Tone = 'ok' | 'warn' | 'fail';
/** Days left at or below which a certificate deserves attention. */
export const WARN_DAYS = 14;
const DAY = 86_400_000;
const HOUR = 3_600_000;

/** ok, warn at 14 days or less, fail once expired; ok when the expiry is unknown. */
export function expiryTone(notAfter: number | null | undefined, now = Date.now()): Tone {
  if (typeof notAfter !== 'number' || !Number.isFinite(notAfter)) return 'ok';
  const left = notAfter - now;
  return left <= 0 ? 'fail' : left <= WARN_DAYS * DAY ? 'warn' : 'ok';
}

/** Whole days left, rounded: 4 d 23 h → 5. Negative once expired. */
export function daysLeft(notAfter: number, now = Date.now()): number {
  return Math.round((notAfter - now) / DAY);
}

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;

/** "in 5 days", "in 6 hours", "in under an hour", "3 days ago", "2 hours ago". */
export function relativeWords(at: number, now = Date.now()): string {
  const diff = at - now;
  const abs = Math.abs(diff);
  let text: string;
  if (abs >= DAY) text = plural(Math.round(abs / DAY), 'day');
  else if (abs >= HOUR) text = plural(Math.floor(abs / HOUR), 'hour');
  else if (abs >= 60_000) text = plural(Math.floor(abs / 60_000), 'minute');
  else return diff >= 0 ? 'in under a minute' : 'just now';
  return diff >= 0 ? `in ${text}` : `${text} ago`;
}

/** "expires in 5 days" / "expired 3 days ago". */
export function expiryWords(notAfter: number, now = Date.now()): string {
  return notAfter > now ? `expires ${relativeWords(notAfter, now)}` : `expired ${relativeWords(notAfter, now)}`;
}

/** The word that carries the tone next to its icon: not colour alone. */
export function toneWord(tone: Tone): string {
  return tone === 'fail' ? 'Expired' : tone === 'warn' ? 'Expires soon' : 'Valid';
}

/** Who signed it, as far as the status tells: only selfSigned is reported. */
export function issuerWords(selfSigned: boolean | null | undefined): string {
  return selfSigned === true ? 'self-signed' : selfSigned === false ? 'CA-signed' : 'unknown';
}

/** The fingerprint for display: first and last groups, the full value stays in the title and the copy. */
export function shortFingerprint(fp: string | null | undefined, head = 14, tail = 8): string {
  if (!fp) return '—';
  return fp.length <= head + tail + 1 ? fp : `${fp.slice(0, head)}…${fp.slice(-tail)}`;
}

export type AcmeTone = 'ok' | 'busy' | 'fail' | 'idle';
export interface AcmeStateView { label: string; tone: AcmeTone; running: boolean }

/** ok / issuing / renewing / failed / idle as a chip: a label (the word), a tone and whether an order runs. */
export function acmeStateView(state: string | null | undefined): AcmeStateView {
  switch (state) {
    case 'ok': return { label: 'ok', tone: 'ok', running: false };
    case 'issuing': return { label: 'issuing', tone: 'busy', running: true };
    case 'renewing': return { label: 'renewing', tone: 'busy', running: true };
    case 'failed': return { label: 'failed', tone: 'fail', running: false };
    case 'idle': return { label: 'idle', tone: 'idle', running: false };
    default: return { label: state || 'unknown', tone: 'idle', running: false };
  }
}

export type AcmeInfo = NonNullable<TlsInfo['acme']>;

/** The directory in words: "Let's Encrypt (production)", "Let's Encrypt staging" or the URL itself. */
export function directoryInfo(directory: string | null | undefined): { label: string; testCa: boolean } {
  const d = (directory ?? '').trim();
  if (!d) return { label: '—', testCa: false };
  if (d === 'production') return { label: 'production', testCa: false };
  if (d === 'staging') return { label: 'staging', testCa: true };
  return { label: d, testCa: /staging|\btest\b|pebble|\bfake\b/i.test(d) };
}

/** Renew is possible when ACME is on, the Vault is online and no order runs. */
export function canRenew(acme: AcmeInfo | null | undefined, online: boolean): boolean {
  return !!acme?.enabled && online && !acmeStateView(acme.state).running;
}

export const POLL_EVERY_MS = 3000;
export const POLL_FOR_MS = 60_000;

/** Whether to keep polling after "Renew now" pressed at `startedAt`: for 60 s, and sooner once the order has finished. */
export function keepPolling(startedAt: number | null, now = Date.now(), acme?: AcmeInfo | null, sawRunning = false): boolean {
  if (startedAt === null || now - startedAt >= POLL_FOR_MS) return false;
  if (sawRunning && acme && !acmeStateView(acme.state).running) return false;
  return true;
}

export interface CertWarning { tone: Tone; text: string }

/** The quiet chip of the Vaults list; null when there is nothing to say. A failed ACME order is reported first. */
export function certificateWarning(tls: TlsInfo | null | undefined, now = Date.now()): CertWarning | null {
  if (!tls) return null;
  if (tls.acme?.enabled && tls.acme.state === 'failed') return { tone: 'fail', text: 'certificate failed' };
  if (typeof tls.notAfter !== 'number') return null;
  const tone = expiryTone(tls.notAfter, now);
  if (tone === 'ok') return null;
  if (tone === 'fail') return { tone, text: 'certificate expired' };
  const left = tls.notAfter - now;
  if (left < DAY) return { tone, text: 'certificate expires today' };
  return { tone, text: `certificate expires in ${plural(daysLeft(tls.notAfter, now), 'day')}` };
}
